import { supabase } from '../lib/supabase';

// Helper function to check if error is schema-related
function isSchemaError(error) {
  if (!error) return false;

  if (error?.code && typeof error?.code === 'string') {
    const errorClass = error?.code?.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') {
      return true;
    }
    if (errorClass === '23') {
      return false;
    }
  }

  if (error?.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
    ];
    return schemaErrorPatterns?.some(pattern => pattern?.test(error?.message));
  }

  return false;
}

// Convert snake_case to camelCase
function toCamelCase(obj) {
  if (Array.isArray(obj)) {
    return obj?.map(item => toCamelCase(item));
  }

  if (obj !== null && typeof obj === 'object' && !(obj instanceof Date)) {
    return Object.keys(obj)?.reduce((acc, key) => {
      const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
      acc[camelKey] = toCamelCase(obj?.[key]);
      return acc;
    }, {});
  }

  return obj;
}

// Convert camelCase to snake_case
function toSnakeCase(obj) {
  if (Array.isArray(obj)) {
    return obj?.map(item => toSnakeCase(item));
  }

  if (obj !== null && typeof obj === 'object' && !(obj instanceof Date)) {
    return Object.keys(obj)?.reduce((acc, key) => {
      const snakeKey = key?.replace(/[A-Z]/g, letter => `_${letter?.toLowerCase()}`);
      acc[snakeKey] = toSnakeCase(obj?.[key]);
      return acc;
    }, {});
  }

  return obj;
}

// ============================================
// ACTIVITY FEED
// ============================================

export const activityService = {
  async getUserActivity(limit = 10) {
    try {
      const { data: user } = await supabase?.auth?.getUser();
      if (!user?.user) throw new Error('User not authenticated');

      const { data, error } = await supabase?.from('activity_feed')?.select('*')?.eq('user_id', user?.user?.id)?.order('timestamp', { ascending: false })?.limit(limit);

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Error fetching activity:', error?.message);
        return [];
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Activity fetch error:', error);
      throw error;
    }
  },

  async createActivity(activityData) {
    try {
      const { data: user } = await supabase?.auth?.getUser();
      if (!user?.user) throw new Error('User not authenticated');

      const snakeData = toSnakeCase({
        userId: user?.user?.id,
        ...activityData
      });

      const { data, error } = await supabase?.from('activity_feed')?.insert(snakeData)?.select()?.single();

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Error creating activity:', error?.message);
        return null;
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Activity creation error:', error);
      throw error;
    }
  }
};

// ============================================
// TOURNAMENTS
// ============================================

export const tournamentsService = {
  async getAll() {
    try {
      const { data: user } = await supabase?.auth?.getUser();
      const userId = user?.user?.id;

      const { data, error } = await supabase?.from('tournaments')?.select(`
          *,
          tournament_teams(
            teams(*)
          ),
          tournament_participants(user_id)
        `)?.order('start_date', { ascending: false });

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Error fetching tournaments:', error?.message);
        return [];
      }

      // Add isParticipating field for each tournament
      const tournamentsWithParticipation = data?.map(tournament => ({
        ...tournament,
        is_participating: userId ? tournament?.tournament_participants?.some(p => p?.user_id === userId) : false
      })) || [];

      return toCamelCase(tournamentsWithParticipation);
    } catch (error) {
      console.error('Tournaments fetch error:', error);
      throw error;
    }
  },

  async getById(tournamentId) {
    try {
      const { data: user } = await supabase?.auth?.getUser();
      const userId = user?.user?.id;

      const { data, error } = await supabase?.from('tournaments')?.select(`
          *,
          tournament_teams(
            teams(*)
          ),
          points_system(*),
          tournament_participants(user_id)
        `)?.eq('id', tournamentId)?.single();

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Error fetching tournament:', error?.message);
        return null;
      }

      // Add isParticipating field
      const tournamentWithParticipation = {
        ...data,
        is_participating: userId ? data?.tournament_participants?.some(p => p?.user_id === userId) : false
      };

      return toCamelCase(tournamentWithParticipation);
    } catch (error) {
      console.error('Tournament fetch error:', error);
      throw error;
    }
  },

  async create(tournamentData) {
    try {
      const { data: user } = await supabase?.auth?.getUser();
      if (!user?.user) throw new Error('User not authenticated');

      const snakeData = toSnakeCase({
        ...tournamentData,
        createdBy: user?.user?.id
      });

      const { data, error } = await supabase?.from('tournaments')?.insert(snakeData)?.select()?.single();

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Error creating tournament:', error?.message);
        return null;
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Tournament creation error:', error);
      throw error;
    }
  },

  async update(tournamentId, tournamentData) {
    try {
      const snakeData = toSnakeCase(tournamentData);

      const { data, error } = await supabase?.from('tournaments')?.update(snakeData)?.eq('id', tournamentId)?.select()?.single();

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Error updating tournament:', error?.message);
        return null;
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Tournament update error:', error);
      throw error;
    }
  },

  async joinTournament(tournamentId) {
    try {
      const { data: user } = await supabase?.auth?.getUser();
      if (!user?.user) throw new Error('User not authenticated');

      const { data, error } = await supabase?.from('tournament_participants')?.insert({
        tournament_id: tournamentId,
        user_id: user?.user?.id
      })?.select()?.single();

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Error joining tournament:', error?.message);
        return null;
      }

      // Track activity: joined tournament
      try {
        const tournament = await this.getById(tournamentId);
        await activityService?.createActivity({
          activityType: 'tournament_join',
          title: 'Joined Tournament',
          description: `You joined ${tournament?.name || 'a tournament'}`,
          icon: 'UserPlus',
          metadata: {
            tournamentId,
            tournamentName: tournament?.name
          }
        });
      } catch (activityError) {
        console.error('Error tracking join activity:', activityError);
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Join tournament error:', error);
      throw error;
    }
  },

  async leaveTournament(tournamentId) {
    try {
      const { data: user } = await supabase?.auth?.getUser();
      if (!user?.user) throw new Error('User not authenticated');

      const { error } = await supabase?.from('tournament_participants')?.delete()?.eq('tournament_id', tournamentId)?.eq('user_id', user?.user?.id);

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Error leaving tournament:', error?.message);
        return false;
      }

      // Track activity: left tournament
      try {
        const tournament = await this.getById(tournamentId);
        await activityService?.createActivity({
          activityType: 'tournament_leave',
          title: 'Left Tournament',
          description: `You left ${tournament?.name || 'a tournament'}`,
          icon: 'UserMinus',
          metadata: {
            tournamentId,
            tournamentName: tournament?.name
          }
        });
      } catch (activityError) {
        console.error('Error tracking leave activity:', activityError);
      }

      return true;
    } catch (error) {
      console.error('Leave tournament error:', error);
      throw error;
    }
  },

  async savePredictionSettings(tournamentId, settings) {
    const { data, error } = await supabase?.from('tournaments')?.update({
      prediction_settings: settings,
      updated_at: new Date()?.toISOString()
    })?.eq('id', tournamentId)?.select()?.single();

    if (error) throw error;

    // Recalculate match_lock_time for all upcoming matches in this tournament
    // so that changes to autoLockHours take effect immediately
    const autoLockHours = settings?.matchLevel?.autoLockHours ?? 1;
    const { data: upcomingMatches } = await supabase
      ?.from('matches')
      ?.select('id, match_date')
      ?.eq('tournament_id', tournamentId)
      ?.gt('match_date', new Date()?.toISOString());

    if (upcomingMatches?.length > 0) {
      const updates = upcomingMatches?.map(match => {
        const matchDate = new Date(match?.match_date);
        const lockTime = new Date(matchDate?.getTime() - autoLockHours * 60 * 60 * 1000);
        return supabase
          ?.from('matches')
          ?.update({ match_lock_time: lockTime?.toISOString() })
          ?.eq('id', match?.id);
      });
      await Promise.all(updates);
    }

    return data;
  },

  async getPredictionSettings(tournamentId) {
    const { data, error } = await supabase?.from('tournaments')?.select('prediction_settings')?.eq('id', tournamentId)?.single();

    if (error) throw error;
    return data?.prediction_settings || null;
  },

  async updateTournamentResults(tournamentId, resultData) {
    try {
      const snakeData = toSnakeCase(resultData);

      const { data, error } = await supabase?.from('tournaments')?.update(snakeData)?.eq('id', tournamentId)?.select()?.single();

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Error updating tournament results:', error?.message);
        return null;
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Tournament results update error:', error);
      throw error;
    }
  },

  async delete(tournamentId) {
    try {
      // Delete related data in order to avoid FK constraint errors
      const relatedTables = [
        'tournament_predictions',
        'tournament_participants',
        'tournament_teams',
        'matches',
        'tournament_squads',
        'deadline_reminders',
        'notifications',
      ];

      for (const table of relatedTables) {
        const { error } = await supabase?.from(table)?.delete()?.eq('tournament_id', tournamentId);
        if (error && !error?.message?.includes('does not exist')) {
          console.warn(`Warning deleting from ${table}:`, error?.message);
        }
      }

      // Delete the tournament itself
      const { error } = await supabase?.from('tournaments')?.delete()?.eq('id', tournamentId);
      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Error deleting tournament:', error?.message);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Tournament delete error:', error);
      throw error;
    }
  },

  async calculateTournamentPoints(tournamentId) {
    try {
      // Fetch tournament results and prediction settings
      const { data: tournament, error: tournamentError } = await supabase
        ?.from('tournaments')
        ?.select('*')
        ?.eq('id', tournamentId)
        ?.single();

      if (tournamentError) {
        console.error('Error fetching tournament:', tournamentError);
        throw tournamentError;
      }

      // Get prediction settings from tournament
      const settings = tournament?.prediction_settings || {};
      const tournamentSettings = settings?.tournamentLevel || {};
      const bonusSettings = settings?.bonusPoints || {};

      // Fetch all tournament predictions for this tournament
      const { data: predictions, error: predictionsError } = await supabase
        ?.from('tournament_predictions')
        ?.select('*')
        ?.eq('tournament_id', tournamentId);

      if (predictionsError) {
        console.error('Error fetching predictions:', predictionsError);
        throw predictionsError;
      }

      // Calculate points for each user
      const userPointsUpdates = [];

      for (const prediction of predictions || []) {
        let tournamentPoints = 0;
        let confidenceBonus = 0;

        // Champion points - only if enabled (using championPrediction from settings)
        if (tournamentSettings?.championPrediction?.enabled && prediction?.champion_team_id === tournament?.champion_team_id) {
          const basePoints = tournamentSettings?.championPrediction?.points || 100;
          tournamentPoints += basePoints;

          // Confidence bonus for champion (only if enabled)
          if (bonusSettings?.confidenceBonus?.enabled && prediction?.champion_confidence) {
            const highConfidence = bonusSettings?.confidenceBonus?.highConfidence || 10;
            const mediumConfidence = bonusSettings?.confidenceBonus?.mediumConfidence || 5;
            // Assuming confidence scale 1-5: 4-5 = high, 2-3 = medium
            if (prediction?.champion_confidence >= 4) {
              confidenceBonus += highConfidence;
            } else if (prediction?.champion_confidence >= 2) {
              confidenceBonus += mediumConfidence;
            }
          }
        }

        // Finalists points - only if enabled (using finalistTeams from settings)
        // Points are multiplied by number of correct predictions
        if (tournamentSettings?.finalistTeams?.enabled) {
          const predictedFinalists = [prediction?.finalist1_id, prediction?.finalist2_id]?.filter(Boolean);
          const actualFinalists = [tournament?.finalist1_id, tournament?.finalist2_id]?.filter(Boolean);

          if (predictedFinalists?.length > 0 && actualFinalists?.length > 0) {
            const correctFinalists = predictedFinalists?.filter(pf => actualFinalists?.includes(pf));
            const pointsPerCorrect = tournamentSettings?.finalistTeams?.points || 60;

            // Multiply points by number of correct predictions
            if (correctFinalists?.length > 0) {
              tournamentPoints += pointsPerCorrect * correctFinalists?.length;

              // Confidence bonus for finalists (only if enabled and both correct)
              if (bonusSettings?.confidenceBonus?.enabled && prediction?.finalists_confidence && correctFinalists?.length === 2) {
                const highConfidence = bonusSettings?.confidenceBonus?.highConfidence || 10;
                const mediumConfidence = bonusSettings?.confidenceBonus?.mediumConfidence || 5;
                if (prediction?.finalists_confidence >= 4) {
                  confidenceBonus += highConfidence;
                } else if (prediction?.finalists_confidence >= 2) {
                  confidenceBonus += mediumConfidence;
                }
              }
            }
          }
        }

        // Semifinalists points - only if enabled (using semifinalistTeams from settings)
        // Points are multiplied by number of correct predictions
        if (tournamentSettings?.semifinalistTeams?.enabled) {
          const predictedSemifinalists = [
            prediction?.semifinalist1_id,
            prediction?.semifinalist2_id,
            prediction?.semifinalist3_id,
            prediction?.semifinalist4_id
          ]?.filter(Boolean);

          const actualSemifinalists = [
            tournament?.semifinalist1_id,
            tournament?.semifinalist2_id,
            tournament?.semifinalist3_id,
            tournament?.semifinalist4_id
          ]?.filter(Boolean);

          if (predictedSemifinalists?.length > 0 && actualSemifinalists?.length > 0) {
            const correctSemifinalists = predictedSemifinalists?.filter(ps => actualSemifinalists?.includes(ps));
            const pointsPerCorrect = tournamentSettings?.semifinalistTeams?.points || 80;

            // Multiply points by number of correct predictions
            if (correctSemifinalists?.length > 0) {
              tournamentPoints += pointsPerCorrect * correctSemifinalists?.length;

              // Confidence bonus for semifinalists (only if enabled and all 4 correct)
              if (bonusSettings?.confidenceBonus?.enabled && prediction?.semifinalists_confidence && correctSemifinalists?.length === 4) {
                const highConfidence = bonusSettings?.confidenceBonus?.highConfidence || 10;
                const mediumConfidence = bonusSettings?.confidenceBonus?.mediumConfidence || 5;
                if (prediction?.semifinalists_confidence >= 4) {
                  confidenceBonus += highConfidence;
                } else if (prediction?.semifinalists_confidence >= 2) {
                  confidenceBonus += mediumConfidence;
                }
              }
            }
          }
        }

        // Player of Tournament points - only if enabled
        if (tournamentSettings?.playerOfTournament?.enabled && prediction?.player_of_tournament_id === tournament?.player_of_tournament_id) {
          const basePoints = tournamentSettings?.playerOfTournament?.points || 50;
          tournamentPoints += basePoints;

          // Confidence bonus for player of tournament (only if enabled)
          if (bonusSettings?.confidenceBonus?.enabled && prediction?.player_confidence) {
            const highConfidence = bonusSettings?.confidenceBonus?.highConfidence || 10;
            const mediumConfidence = bonusSettings?.confidenceBonus?.mediumConfidence || 5;
            if (prediction?.player_confidence >= 4) {
              confidenceBonus += highConfidence;
            } else if (prediction?.player_confidence >= 2) {
              confidenceBonus += mediumConfidence;
            }
          }
        }

        // Top 4 Teams points - only if enabled
        if (tournamentSettings?.top4Teams?.enabled) {
          const predictedTop4 = [
            prediction?.top4_team1_id,
            prediction?.top4_team2_id,
            prediction?.top4_team3_id,
            prediction?.top4_team4_id
          ];
          const actualTop4 = [
            tournament?.top4_team1_id,
            tournament?.top4_team2_id,
            tournament?.top4_team3_id,
            tournament?.top4_team4_id
          ];

          const pointsPerCorrectTeam = tournamentSettings?.top4Teams?.points || 10;
          const pointsPerCorrectOrder = tournamentSettings?.top4Teams?.orderPoints || 5;

          // Points for each correct team (regardless of position)
          const actualTop4Set = new Set(actualTop4?.filter(Boolean));
          for (const predictedTeam of predictedTop4) {
            if (predictedTeam && actualTop4Set?.has(predictedTeam)) {
              tournamentPoints += pointsPerCorrectTeam;
            }
          }

          // Additional points for each correct order position
          for (let i = 0; i < 4; i++) {
            if (predictedTop4?.[i] && actualTop4?.[i] && predictedTop4?.[i] === actualTop4?.[i]) {
              tournamentPoints += pointsPerCorrectOrder;
            }
          }
        }

        // Super Over points - only if enabled
        if (tournamentSettings?.superOverTeam?.enabled) {
          const predictedSuperOverTeam = prediction?.super_over_team_id;
          const actualSuperOverTeams = tournament?.super_over_team_ids || [];

          if (predictedSuperOverTeam && actualSuperOverTeams?.length > 0 && actualSuperOverTeams?.includes(predictedSuperOverTeam)) {
            const basePoints = tournamentSettings?.superOverTeam?.points || 15;
            tournamentPoints += basePoints;
          }
        }

        userPointsUpdates?.push({
          userId: prediction?.user_id,
          tournamentPoints,
          confidenceBonus
        });
      }

      // Update leaderboard with tournament points (recalculate from scratch, don't add)
      for (const update of userPointsUpdates) {
        // Fetch current leaderboard entry
        const { data: currentEntry, error: fetchError } = await supabase
          ?.from('leaderboard')?.select('*')?.eq('tournament_id', tournamentId)?.eq('user_id', update?.userId)
          ?.maybeSingle();

        if (fetchError) {
          console.error('Error fetching leaderboard entry:', fetchError);
          continue;
        }

        let matchPoints = currentEntry?.match_prediction_points || 0;
        const bonusPoints = currentEntry?.bonus_points || 0;

        // Recalculate match-level confidence fresh from match predictions
        // to avoid double-counting tournament confidence on re-runs.
        let matchConfidenceOnly = 0;
        const { data: userMatchPreds } = await supabase
          ?.from('match_predictions')
          ?.select('*, matches!inner(tournament_id, winner_id, toss_winner_id, batting_first_id, man_of_match_id, highest_scorer_id, highest_wicket_taker_id)')
          ?.eq('user_id', update?.userId)
          ?.eq('matches.tournament_id', tournamentId);

        if (userMatchPreds && bonusSettings?.confidenceBonus?.enabled) {
          const highConf = bonusSettings?.confidenceBonus?.highConfidence || 10;
          const medConf = bonusSettings?.confidenceBonus?.mediumConfidence || 5;
          for (const mp of userMatchPreds) {
            const md = mp?.matches;
            const addConf = (predField, actualField, confField) => {
              if (mp?.[predField] && md?.[actualField] && mp?.[predField] === md?.[actualField] && mp?.[confField]) {
                if (mp?.[confField] >= 4) matchConfidenceOnly += highConf;
                else if (mp?.[confField] >= 2) matchConfidenceOnly += medConf;
              }
            };
            addConf('winner_id', 'winner_id', 'winner_confidence');
            addConf('toss_winner_id', 'toss_winner_id', 'toss_confidence');
            addConf('batting_first_id', 'batting_first_id', 'batting_confidence');
            addConf('man_of_match_id', 'man_of_match_id', 'motm_confidence');
            addConf('highest_scorer_id', 'highest_scorer_id', 'scorer_confidence');
            addConf('highest_wicket_taker_id', 'highest_wicket_taker_id', 'wicket_confidence');
          }
        }

        let totalConfidenceBonus = matchConfidenceOnly + update?.confidenceBonus;
        const totalPoints = update?.tournamentPoints + matchPoints + bonusPoints + totalConfidenceBonus;

        // Upsert leaderboard entry
        const { error: updateError } = await supabase
          ?.from('leaderboard')
          ?.upsert({
            tournament_id: tournamentId,
            user_id: update?.userId,
            tournament_prediction_points: update?.tournamentPoints,
            match_prediction_points: matchPoints,
            bonus_points: bonusPoints,
            confidence_bonus: totalConfidenceBonus,
            total_points: totalPoints,
            rank: currentEntry?.rank || 999,
            updated_at: new Date()?.toISOString()
          }, { onConflict: 'tournament_id,user_id' });

        if (updateError) {
          console.error('Error updating leaderboard:', updateError);
        }
      }

      // Recalculate ranks
      const { data: leaderboard, error: leaderboardError } = await supabase
        ?.from('leaderboard')?.select('*')?.eq('tournament_id', tournamentId)?.order('total_points', { ascending: false });

      if (leaderboardError) {
        console.error('Error fetching leaderboard for ranking:', leaderboardError);
        throw leaderboardError;
      }

      // Update ranks with tiered sorting: total_points → accuracy → correct_predictions
      const sortedLeaderboard = [...(leaderboard || [])]?.sort((a, b) => {
        if (b?.total_points !== a?.total_points) return b?.total_points - a?.total_points;
        if (b?.accuracy !== a?.accuracy) return b?.accuracy - a?.accuracy;
        return (b?.correct_predictions || 0) - (a?.correct_predictions || 0);
      });

      for (let i = 0; i < sortedLeaderboard?.length; i++) {
        await supabase
          ?.from('leaderboard')
          ?.update({ rank: i + 1 })
          ?.eq('id', sortedLeaderboard?.[i]?.id);
      }

      return {
        success: true,
        affectedUsers: userPointsUpdates?.length,
        totalPointsAwarded: userPointsUpdates?.reduce((sum, u) => sum + u?.tournamentPoints + u?.confidenceBonus, 0)
      };
    } catch (error) {
      console.error('Tournament points calculation error:', error);
      throw error;
    }
  },

  async calculateMatchPoints(matchId) {
    try {
      // Fetch match results
      const { data: match, error: matchError } = await supabase
        ?.from('matches')?.select('*')?.eq('id', matchId)
        ?.single();

      if (matchError) {
        console.error('Error fetching match:', matchError);
        throw matchError;
      }

      // Fetch tournament with prediction settings
      const { data: tournament, error: tournamentError } = await supabase
        ?.from('tournaments')?.select('prediction_settings')?.eq('id', match?.tournament_id)
        ?.single();

      if (tournamentError) {
        console.error('Error fetching tournament settings:', tournamentError);
        throw tournamentError;
      }

      const settings = tournament?.prediction_settings || {};
      const matchSettings = settings?.matchLevel || {};
      const bonusSettings = settings?.bonusPoints || {};

      // Fetch all match predictions for this match
      const { data: predictions, error: predictionsError } = await supabase
        ?.from('match_predictions')?.select('*')?.eq('match_id', matchId);

      if (predictionsError) {
        console.error('Error fetching match predictions:', predictionsError);
        throw predictionsError;
      }

      // Calculate points for each user
      const userPointsUpdates = [];

      for (const prediction of predictions || []) {
        let matchPoints = 0;
        let correctPredictions = 0;
        let totalPredictions = 0;
        let confidenceBonus = 0;

        // Winner prediction
        if (matchSettings?.matchWinner?.enabled && prediction?.winner_id && match?.winner_id) {
          totalPredictions++;
          if (prediction?.winner_id === match?.winner_id) {
            const basePoints = matchSettings?.matchWinner?.points || 10;
            matchPoints += basePoints;
            correctPredictions++;

            // Confidence bonus for winner (only if enabled)
            if (bonusSettings?.confidenceBonus?.enabled && prediction?.winner_confidence) {
              const highConfidence = bonusSettings?.confidenceBonus?.highConfidence || 10;
              const mediumConfidence = bonusSettings?.confidenceBonus?.mediumConfidence || 5;
              // Assuming confidence scale 1-5: 4-5 = high, 2-3 = medium
              if (prediction?.winner_confidence >= 4) {
                confidenceBonus += highConfidence;
              } else if (prediction?.winner_confidence >= 2) {
                confidenceBonus += mediumConfidence;
              }
            }
          }
        }

        // Toss winner prediction
        if (matchSettings?.tossWinner?.enabled && prediction?.toss_winner_id && match?.toss_winner_id) {
          totalPredictions++;
          if (prediction?.toss_winner_id === match?.toss_winner_id) {
            matchPoints += matchSettings?.tossWinner?.points || 5;
            correctPredictions++;
          }
        }

        // Batting first prediction
        if (matchSettings?.battingFirst?.enabled && prediction?.batting_first_id && match?.batting_first_id) {
          totalPredictions++;
          if (prediction?.batting_first_id === match?.batting_first_id) {
            matchPoints += matchSettings?.battingFirst?.points || 5;
            correctPredictions++;
          }
        }

        // Man of the match prediction
        if (matchSettings?.manOfMatch?.enabled && prediction?.man_of_match_id && match?.man_of_match_id) {
          totalPredictions++;
          if (prediction?.man_of_match_id === match?.man_of_match_id) {
            const basePoints = matchSettings?.manOfMatch?.points || 15;
            matchPoints += basePoints;
            correctPredictions++;

            // Confidence bonus for MOTM (only if enabled)
            if (bonusSettings?.confidenceBonus?.enabled && prediction?.motm_confidence) {
              const highConfidence = bonusSettings?.confidenceBonus?.highConfidence || 10;
              const mediumConfidence = bonusSettings?.confidenceBonus?.mediumConfidence || 5;
              // Assuming confidence scale 1-5: 4-5 = high, 2-3 = medium
              if (prediction?.motm_confidence >= 4) {
                confidenceBonus += highConfidence;
              } else if (prediction?.motm_confidence >= 2) {
                confidenceBonus += mediumConfidence;
              }
            }
          }
        }

        // Highest scorer prediction
        if (matchSettings?.highestRunScorer?.enabled && prediction?.highest_scorer_id && match?.highest_scorer_id) {
          totalPredictions++;
          if (prediction?.highest_scorer_id === match?.highest_scorer_id) {
            const basePoints = matchSettings?.highestRunScorer?.points || 20;
            matchPoints += basePoints;
            correctPredictions++;

            // Confidence bonus for scorer (only if enabled)
            if (bonusSettings?.confidenceBonus?.enabled && prediction?.scorer_confidence) {
              const highConfidence = bonusSettings?.confidenceBonus?.highConfidence || 10;
              const mediumConfidence = bonusSettings?.confidenceBonus?.mediumConfidence || 5;
              // Assuming confidence scale 1-5: 4-5 = high, 2-3 = medium
              if (prediction?.scorer_confidence >= 4) {
                confidenceBonus += highConfidence;
              } else if (prediction?.scorer_confidence >= 2) {
                confidenceBonus += mediumConfidence;
              }
            }
          }
        }

        // Highest wicket taker prediction
        if (matchSettings?.highestWicketTaker?.enabled && prediction?.highest_wicket_taker_id && match?.highest_wicket_taker_id) {
          totalPredictions++;
          if (prediction?.highest_wicket_taker_id === match?.highest_wicket_taker_id) {
            const basePoints = matchSettings?.highestWicketTaker?.points || 20;
            matchPoints += basePoints;
            correctPredictions++;

            // Confidence bonus for wicket taker (only if enabled)
            if (bonusSettings?.confidenceBonus?.enabled && prediction?.wicket_confidence) {
              const highConfidence = bonusSettings?.confidenceBonus?.highConfidence || 10;
              const mediumConfidence = bonusSettings?.confidenceBonus?.mediumConfidence || 5;
              // Assuming confidence scale 1-5: 4-5 = high, 2-3 = medium
              if (prediction?.wicket_confidence >= 4) {
                confidenceBonus += highConfidence;
              } else if (prediction?.wicket_confidence >= 2) {
                confidenceBonus += mediumConfidence;
              }
            }
          }
        }

        userPointsUpdates?.push({
          userId: prediction?.user_id,
          matchPoints,
          confidenceBonus,
          correctPredictions,
          totalPredictions
        });
      }

      // Update leaderboard with match points
      for (const update of userPointsUpdates) {
        // Fetch current leaderboard entry
        const { data: currentEntry, error: fetchError } = await supabase
          ?.from('leaderboard')
          ?.select('*')
          ?.eq('tournament_id', match?.tournament_id)
          ?.eq('user_id', update?.userId)
          ?.maybeSingle();

        if (fetchError) {
          console.error('Error fetching leaderboard entry:', fetchError);
          continue;
        }

        // Recalculate ALL match points for this user in this tournament from scratch
        // This ensures edit actions update correctly instead of adding
        const { data: allUserMatches, error: allMatchesError } = await supabase
          ?.from('matches')
          ?.select('id, winner_id, toss_winner_id, batting_first_id, man_of_match_id, highest_scorer_id, highest_wicket_taker_id, tournament_id')
          ?.eq('tournament_id', match?.tournament_id)
          ?.eq('results_status', 'published');

        if (allMatchesError) {
          console.error('Error fetching all matches:', allMatchesError);
          continue;
        }

        const { data: allUserPredictions, error: allPredictionsError } = await supabase
          ?.from('match_predictions')
          ?.select('*')
          ?.eq('user_id', update?.userId)
          ?.in('match_id', allUserMatches?.map(m => m?.id) || []);

        if (allPredictionsError) {
          console.error('Error fetching all user predictions:', allPredictionsError);
          continue;
        }

        // Recalculate from scratch
        let totalMatchPoints = 0;
        let totalConfidenceBonus = 0;
        let totalCorrectPredictions = 0;
        let totalPredictionsCount = 0;

        for (const userPred of allUserPredictions || []) {
          const matchData = allUserMatches?.find(m => m?.id === userPred?.match_id);
          if (!matchData) continue;

          let matchPts = 0;
          let confBonus = 0;
          let correct = 0;
          let total = 0;

          // Winner prediction
          if (matchSettings?.matchWinner?.enabled && userPred?.winner_id && matchData?.winner_id) {
            total++;
            if (userPred?.winner_id === matchData?.winner_id) {
              matchPts += matchSettings?.matchWinner?.points || 10;
              correct++;

              if (bonusSettings?.confidenceBonus?.enabled && userPred?.winner_confidence) {
                const highConfidence = bonusSettings?.confidenceBonus?.highConfidence || 10;
                const mediumConfidence = bonusSettings?.confidenceBonus?.mediumConfidence || 5;
                if (userPred?.winner_confidence >= 4) {
                  confBonus += highConfidence;
                } else if (userPred?.winner_confidence >= 2) {
                  confBonus += mediumConfidence;
                }
              }
            }
          }

          // Toss winner
          if (matchSettings?.tossWinner?.enabled && userPred?.toss_winner_id && matchData?.toss_winner_id) {
            total++;
            if (userPred?.toss_winner_id === matchData?.toss_winner_id) {
              matchPts += matchSettings?.tossWinner?.points || 5;
              correct++;
            }
          }

          // Batting first
          if (matchSettings?.battingFirst?.enabled && userPred?.batting_first_id && matchData?.batting_first_id) {
            total++;
            if (userPred?.batting_first_id === matchData?.batting_first_id) {
              matchPts += matchSettings?.battingFirst?.points || 5;
              correct++;
            }
          }

          // Man of the match
          if (matchSettings?.manOfMatch?.enabled && userPred?.man_of_match_id && matchData?.man_of_match_id) {
            total++;
            if (userPred?.man_of_match_id === matchData?.man_of_match_id) {
              matchPts += matchSettings?.manOfMatch?.points || 15;
              correct++;

              if (bonusSettings?.confidenceBonus?.enabled && userPred?.motm_confidence) {
                const highConfidence = bonusSettings?.confidenceBonus?.highConfidence || 10;
                const mediumConfidence = bonusSettings?.confidenceBonus?.mediumConfidence || 5;
                if (userPred?.motm_confidence >= 4) {
                  confBonus += highConfidence;
                } else if (userPred?.motm_confidence >= 2) {
                  confBonus += mediumConfidence;
                }
              }
            }
          }

          // Highest scorer
          if (matchSettings?.highestRunScorer?.enabled && userPred?.highest_scorer_id && matchData?.highest_scorer_id) {
            total++;
            if (userPred?.highest_scorer_id === matchData?.highest_scorer_id) {
              matchPts += matchSettings?.highestRunScorer?.points || 20;
              correct++;

              if (bonusSettings?.confidenceBonus?.enabled && userPred?.scorer_confidence) {
                const highConfidence = bonusSettings?.confidenceBonus?.highConfidence || 10;
                const mediumConfidence = bonusSettings?.confidenceBonus?.mediumConfidence || 5;
                if (userPred?.scorer_confidence >= 4) {
                  confBonus += highConfidence;
                } else if (userPred?.scorer_confidence >= 2) {
                  confBonus += mediumConfidence;
                }
              }
            }
          }

          // Highest wicket taker
          if (matchSettings?.highestWicketTaker?.enabled && userPred?.highest_wicket_taker_id && matchData?.highest_wicket_taker_id) {
            total++;
            if (userPred?.highest_wicket_taker_id === matchData?.highest_wicket_taker_id) {
              matchPts += matchSettings?.highestWicketTaker?.points || 20;
              correct++;

              if (bonusSettings?.confidenceBonus?.enabled && userPred?.wicket_confidence) {
                const highConfidence = bonusSettings?.confidenceBonus?.highConfidence || 10;
                const mediumConfidence = bonusSettings?.confidenceBonus?.mediumConfidence || 5;
                if (userPred?.wicket_confidence >= 4) {
                  confBonus += highConfidence;
                } else if (userPred?.wicket_confidence >= 2) {
                  confBonus += mediumConfidence;
                }
              }
            }
          }

          totalMatchPoints += matchPts;
          totalConfidenceBonus += confBonus;
          totalCorrectPredictions += correct;
          totalPredictionsCount += total;
        }

        let tournamentPoints = currentEntry?.tournament_prediction_points || 0;
        const bonusPoints = currentEntry?.bonus_points || 0;
        const totalPoints = tournamentPoints + totalMatchPoints + bonusPoints + totalConfidenceBonus;
        const accuracy = totalPredictionsCount > 0 ? (totalCorrectPredictions / totalPredictionsCount) * 100 : 0;

        // Upsert leaderboard entry
        const { error: updateError } = await supabase
          ?.from('leaderboard')
          ?.upsert({
            tournament_id: match?.tournament_id,
            user_id: update?.userId,
            tournament_prediction_points: tournamentPoints,
            match_prediction_points: totalMatchPoints,
            bonus_points: bonusPoints,
            confidence_bonus: totalConfidenceBonus,
            correct_predictions: totalCorrectPredictions,
            total_predictions: totalPredictionsCount,
            accuracy: accuracy,
            total_points: totalPoints,
            rank: currentEntry?.rank || 999,
            updated_at: new Date()?.toISOString()
          }, { onConflict: 'tournament_id,user_id' });

        if (updateError) {
          console.error('Error updating leaderboard:', updateError);
        }
      }

      // Recalculate ranks
      const { data: leaderboard, error: leaderboardError } = await supabase
        ?.from('leaderboard')
        ?.select('*')
        ?.eq('tournament_id', match?.tournament_id)
        ?.order('total_points', { ascending: false });

      if (leaderboardError) {
        console.error('Error fetching leaderboard for ranking:', leaderboardError);
        throw leaderboardError;
      }

      // Update ranks with tiered sorting: total_points → accuracy → correct_predictions
      const sortedLeaderboard = [...(leaderboard || [])]?.sort((a, b) => {
        if (b?.total_points !== a?.total_points) return b?.total_points - a?.total_points;
        if (b?.accuracy !== a?.accuracy) return b?.accuracy - a?.accuracy;
        return (b?.correct_predictions || 0) - (a?.correct_predictions || 0);
      });

      for (let i = 0; i < sortedLeaderboard?.length; i++) {
        await supabase
          ?.from('leaderboard')
          ?.update({ rank: i + 1 })
          ?.eq('id', sortedLeaderboard?.[i]?.id);
      }

      return {
        success: true,
        affectedUsers: userPointsUpdates?.length,
        totalPointsAwarded: userPointsUpdates?.reduce((sum, u) => sum + u?.matchPoints + u?.confidenceBonus, 0)
      };
    } catch (error) {
      console.error('Match points calculation error:', error);
      throw error;
    }
  }
};

// ============================================
// MATCHES
// ============================================

export const matchesService = {
  async getByTournament(tournamentId) {
    try {
      if (!supabase) {
        console.error('Supabase client not initialized');
        return [];
      }

      if (!tournamentId) {
        console.error('Tournament ID is required');
        return [];
      }

      const { data, error } = await supabase?.from('matches')?.select(`
          *,
          team1:teams!matches_team1_id_fkey(*),
          team2:teams!matches_team2_id_fkey(*),
          winner:teams!matches_winner_id_fkey(*)
        `)?.eq('tournament_id', tournamentId)?.order('match_date', { ascending: true });

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Error fetching matches:', error?.message);
        return [];
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Matches fetch error:', error);
      throw error;
    }
  },

  async getById(matchId) {
    try {
      const { data, error } = await supabase?.from('matches')?.select(`
          *,
          team1:teams!matches_team1_id_fkey(*),
          team2:teams!matches_team2_id_fkey(*),
          tournament:tournaments(
            *,
            players:tournament_squads(
              player:players(*)
            )
          )
        `)?.eq('id', matchId)?.single();

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Error fetching match:', error?.message);
        return null;
      }

      // Transform players array for easier access
      if (data?.tournament?.players) {
        data.tournament.players = data?.tournament?.players?.map(p => p?.player);
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Match fetch error:', error);
      throw error;
    }
  },

  async updateResult(matchId, resultData) {
    try {
      // Convert empty strings to null for UUID fields
      const cleanedData = {
        ...resultData,
        winnerId: (resultData?.winnerId && resultData?.winnerId !== 'no_result') ? resultData?.winnerId : null,
        manOfMatchId: resultData?.manOfMatchId || null,
        tossWinnerId: resultData?.tossWinnerId || null,
        battingFirstId: resultData?.battingFirstId || null,
        highestScorerId: resultData?.highestScorerId || null,
        highestWicketTakerId: resultData?.highestWicketTakerId || null,
        // Convert empty strings to null for integer fields
        team1Score: resultData?.team1Score !== '' && resultData?.team1Score != null ? Number(resultData?.team1Score) : null,
        team1Wickets: resultData?.team1Wickets !== '' && resultData?.team1Wickets != null ? Number(resultData?.team1Wickets) : null,
        team2Score: resultData?.team2Score !== '' && resultData?.team2Score != null ? Number(resultData?.team2Score) : null,
        team2Wickets: resultData?.team2Wickets !== '' && resultData?.team2Wickets != null ? Number(resultData?.team2Wickets) : null,
      };

      const snakeData = toSnakeCase(cleanedData);

      const { data, error } = await supabase?.from('matches')?.update(snakeData)?.eq('id', matchId)?.select()?.single();

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Error updating match result:', error?.message);
        return null;
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Match result update error:', error);
      throw error;
    }
  },

  async create(matchData) {
    try {
      const snakeData = toSnakeCase(matchData);

      const { data, error } = await supabase?.from('matches')?.insert(snakeData)?.select()?.single();

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Error creating match:', error?.message);
        return null;
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Match creation error:', error);
      throw error;
    }
  },

  async update(matchId, matchData) {
    try {
      const snakeData = toSnakeCase(matchData);

      const { data, error } = await supabase?.from('matches')?.update(snakeData)?.eq('id', matchId)?.select()?.single();

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Error updating match:', error?.message);
        return null;
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Match update error:', error);
      throw error;
    }
  },

  async delete(matchId) {
    try {
      const { error } = await supabase?.from('matches')?.delete()?.eq('id', matchId);

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Error deleting match:', error?.message);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Match deletion error:', error);
      throw error;
    }
  },

  // Enable predictions for all upcoming matches of a tournament based on autoEnableDays setting
  async enableMatchPredictionsBySettings(tournamentId, autoEnableDays) {
    try {
      // Fetch all upcoming/scheduled matches for this tournament
      const { data: matches, error: fetchError } = await supabase
        ?.from('matches')
        ?.select('id, match_date, predictions_enabled')
        ?.eq('tournament_id', tournamentId)
        ?.in('status', ['upcoming', 'live']);

      if (fetchError) {
        if (isSchemaError(fetchError)) throw fetchError;
        console.error('Error fetching matches for enabling predictions:', fetchError?.message);
        return { enabled: 0, skipped: 0 };
      }

      const now = new Date();
      const matchesToEnable = [];

      for (const match of matches || []) {
        const matchDate = new Date(match?.match_date);
        // Calculate the date/time when predictions should open
        const enableDate = new Date(matchDate);
        enableDate?.setDate(enableDate?.getDate() - (autoEnableDays || 0));

        // Enable if we've passed the enable date (or autoEnableDays is 0 = always enabled)
        if (autoEnableDays === 0 || now >= enableDate) {
          matchesToEnable?.push(match?.id);
        }
      }

      if (matchesToEnable?.length > 0) {
        const { error: updateError } = await supabase
          ?.from('matches')
          ?.update({ predictions_enabled: true })
          ?.in('id', matchesToEnable);

        if (updateError) {
          if (isSchemaError(updateError)) throw updateError;
          console.error('Error enabling match predictions:', updateError?.message);
          return { enabled: 0, skipped: matches?.length || 0 };
        }
      }

      return {
        enabled: matchesToEnable?.length,
        skipped: (matches?.length || 0) - matchesToEnable?.length
      };
    } catch (error) {
      console.error('Enable match predictions error:', error);
      throw error;
    }
  },

  async getUpcomingForUser(userId, limit = 5) {
    try {
      const { data, error } = await supabase
        ?.from('matches')
        ?.select(`
          *,
          team1:teams!matches_team1_id_fkey(*),
          team2:teams!matches_team2_id_fkey(*),
          tournament:tournaments!inner(
            id,
            name,
            format,
            tournament_participants!inner(
              user_id
            )
          ),
          match_predictions!left(
            id,
            user_id
          )
        `)
        ?.eq('tournament.tournament_participants.user_id', userId)
        ?.eq('status', 'upcoming')
        ?.eq('predictions_enabled', true)
        ?.gte('match_date', new Date()?.toISOString())
        ?.order('match_date', { ascending: true })
        ?.limit(limit);

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Error fetching upcoming matches:', error?.message);
        return [];
      }

      // Transform data to include hasPredicted flag
      const transformedData = data?.map(match => ({
        ...match,
        hasPredicted: match?.match_predictions?.some(pred => pred?.user_id === userId) || false
      })) || [];

      return toCamelCase(transformedData);
    } catch (error) {
      console.error('Upcoming matches fetch error:', error);
      throw error;
    }
  }
};

// ============================================
// PREDICTIONS
// ============================================

export const predictionsService = {
  async getTournamentPrediction(tournamentId) {
    try {
      const { data: user } = await supabase?.auth?.getUser();
      if (!user?.user) throw new Error('User not authenticated');

      const { data, error } = await supabase?.from('tournament_predictions')?.select('*')?.eq('tournament_id', tournamentId)?.eq('user_id', user?.user?.id)?.maybeSingle();

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Error fetching tournament prediction:', error?.message);
        return null;
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Tournament prediction fetch error:', error);
      throw error;
    }
  },

  async checkTournamentLockStatus(tournamentId) {
    try {
      const { data, error } = await supabase
        ?.from('tournaments')
        ?.select('tournament_lock_time')
        ?.eq('id', tournamentId)
        ?.single();

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Error checking tournament lock status:', error?.message);
        return { isLocked: false, lockTime: null };
      }

      const lockTime = data?.tournament_lock_time ? new Date(data?.tournament_lock_time) : null;
      const isLocked = lockTime ? new Date() >= lockTime : false;

      return { isLocked, lockTime };
    } catch (error) {
      console.error('Tournament lock status check error:', error);
      return { isLocked: false, lockTime: null };
    }
  },

  // Check if tournament predictions are locked (with override check)
  async checkTournamentPredictionLock(tournamentId) {
    try {
      const { data: tournament, error } = await supabase
        ?.from('tournaments')
        ?.select('tournament_lock_time, prediction_settings')
        ?.eq('id', tournamentId)
        ?.single();

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Error fetching tournament lock status:', error?.message);
        return { isLocked: false, canOverride: false };
      }

      const lockTime = tournament?.tournament_lock_time;
      const extendedLockHours = tournament?.prediction_settings?.tournamentLevel?.extendedLockHours || 0;

      if (!lockTime) {
        return {
          isLocked: false,
          effectivelyLocked: false,
          lockTime: null,
          extendedLockTime: null
        };
      }

      const baseLockTime = new Date(lockTime);
      const extendedLockTime = new Date(baseLockTime.getTime() + extendedLockHours * 60 * 60 * 1000);
      const now = new Date();

      const isBaseLocked = now >= baseLockTime;
      const isEffectivelyLocked = now >= extendedLockTime;

      return {
        isLocked: isBaseLocked,
        effectivelyLocked: isEffectivelyLocked,
        lockTime: baseLockTime,
        extendedLockTime: extendedLockHours > 0 ? extendedLockTime : baseLockTime
      };
    } catch (error) {
      console.error('Tournament prediction lock check error:', error);
      return { isLocked: false, canOverride: false };
    }
  },

  async saveTournamentPrediction(tournamentId, predictionData) {
    try {
      const { data: user } = await supabase?.auth?.getUser();
      if (!user?.user) throw new Error('User not authenticated');

      // Check if predictions are locked (with override check)
      const { effectivelyLocked } = await this.checkTournamentPredictionLock(tournamentId);
      if (effectivelyLocked) {
        throw new Error('Tournament predictions are locked. The deadline has passed.');
      }

      const snakeData = toSnakeCase({
        tournamentId,
        userId: user?.user?.id,
        ...predictionData
      });

      const { data, error } = await supabase?.from('tournament_predictions')?.upsert(snakeData, { onConflict: 'tournament_id,user_id' })?.select()?.single();

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Error saving tournament prediction:', error?.message);
        return null;
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Tournament prediction save error:', error);
      throw error;
    }
  },

  async getMatchPrediction(matchId) {
    try {
      const { data: user } = await supabase?.auth?.getUser();
      if (!user?.user) return null;

      const { data, error } = await supabase?.from('match_predictions')?.select('*')?.eq('match_id', matchId)?.eq('user_id', user?.user?.id)?.maybeSingle();

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Error fetching match prediction:', error?.message);
        return null;
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Match prediction fetch error:', error);
      return null;
    }
  },

  async checkMatchLockStatus(matchId) {
    try {
      const { data, error } = await supabase
        ?.from('matches')
        ?.select('match_lock_time, match_date, predictions_enabled, predictions_open_time, tournament_id')
        ?.eq('id', matchId)
        ?.single();

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Error checking match lock status:', error?.message);
        return { isLocked: false, lockTime: null, predictionsEnabled: true, predictionsOpenTime: null };
      }

      // Fetch tournament's autoLockHours to compute the effective lock time
      // This ensures autoLockHours=0 (lock at match start) is respected even if
      // the DB trigger hasn't recalculated match_lock_time yet
      let effectiveLockTime = data?.match_lock_time ? new Date(data?.match_lock_time) : null;
      let resolvedAutoLockHours = 1; // default

      if (data?.tournament_id && data?.match_date) {
        const { data: tournament } = await supabase
          ?.from('tournaments')
          ?.select('prediction_settings')
          ?.eq('id', data?.tournament_id)
          ?.single();

        if (tournament?.prediction_settings) {
          const autoLockHours = tournament?.prediction_settings?.matchLevel?.autoLockHours;
          // Only override if autoLockHours is explicitly defined (including 0)
          if (autoLockHours !== undefined && autoLockHours !== null) {
            const matchDate = new Date(data?.match_date);
            effectiveLockTime = new Date(matchDate?.getTime() - Number(autoLockHours) * 60 * 60 * 1000);
            resolvedAutoLockHours = Number(autoLockHours);
          }
        }
      }

      const isLocked = effectiveLockTime ? new Date() >= effectiveLockTime : false;
      const predictionsEnabled = data?.predictions_enabled ?? true;
      const predictionsOpenTime = data?.predictions_open_time ? new Date(data?.predictions_open_time) : null;

      return { isLocked, lockTime: effectiveLockTime, predictionsEnabled, predictionsOpenTime, autoLockHours: resolvedAutoLockHours };
    } catch (error) {
      console.error('Match lock status check error:', error);
      return { isLocked: false, lockTime: null, predictionsEnabled: true, predictionsOpenTime: null };
    }
  },

  async saveMatchPrediction(matchId, predictionData) {
    try {
      const { data: user } = await supabase?.auth?.getUser();
      if (!user?.user) throw new Error('User not authenticated');

      // Check if predictions are locked
      const { isLocked, predictionsEnabled } = await this.checkMatchLockStatus(matchId);
      if (isLocked) {
        throw new Error('Match predictions are locked. The deadline has passed.');
      }
      if (!predictionsEnabled) {
        throw new Error('Predictions are not yet open for this match.');
      }

      const snakeData = toSnakeCase({
        matchId,
        userId: user?.user?.id,
        ...predictionData
      });

      const { data, error } = await supabase?.from('match_predictions')?.upsert(snakeData, { onConflict: 'match_id,user_id' })?.select()?.single();

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Error saving match prediction:', error?.message);
        return null;
      }

      // Track activity: made match prediction
      try {
        const match = await matchesService?.getById(matchId);
        const matchDescription = match?.team1?.name && match?.team2?.name
          ? `${match?.team1?.name} vs ${match?.team2?.name}`
          : 'a match';

        await activityService?.createActivity({
          activityType: 'match_prediction',
          title: 'Match Prediction Submitted',
          description: `You submitted predictions for ${matchDescription}`,
          icon: 'Target',
          metadata: {
            matchId,
            tournamentId: match?.tournamentId,
            matchDescription
          }
        });
      } catch (activityError) {
        console.error('Error tracking match prediction activity:', activityError);
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Match prediction save error:', error);
      throw error;
    }
  }
};

// ============================================
// LEADERBOARD
// ============================================

export const leaderboardService = {
  async getByTournament(tournamentId) {
    try {
      // Fetch leaderboard entries
      const { data: leaderboardData, error: leaderboardError } = await supabase?.from('leaderboard')?.select(`
          *,
          user_profiles(*)
        `)?.eq('tournament_id', tournamentId)?.order('rank', { ascending: true });

      if (leaderboardError) {
        if (isSchemaError(leaderboardError)) throw leaderboardError;
        console.error('Leaderboard fetch error:', leaderboardError);
      }

      // Fetch all tournament participants
      const { data: participantsData, error: participantsError } = await supabase
        ?.from('tournament_participants')
        ?.select(`
          user_id,
          user_profiles(*)
        `)
        ?.eq('tournament_id', tournamentId);

      if (participantsError) {
        if (isSchemaError(participantsError)) throw participantsError;
        console.error('Participants fetch error:', participantsError);
      }

      // If no participants data, fall back to leaderboard data directly
      if (!participantsData || participantsData?.length === 0) {
        const sortedLeaderboard = (leaderboardData || [])?.sort((a, b) => {
          if (b?.total_points !== a?.total_points) return b?.total_points - a?.total_points;
          if (b?.accuracy !== a?.accuracy) return b?.accuracy - a?.accuracy;
          return (b?.correct_predictions || 0) - (a?.correct_predictions || 0);
        });
        sortedLeaderboard?.forEach((entry, index) => {
          entry.rank = index + 1;
        });
        return toCamelCase(sortedLeaderboard);
      }

      // Create a map of leaderboard entries by user_id
      const leaderboardMap = new Map();
      (leaderboardData || [])?.forEach(entry => {
        leaderboardMap?.set(entry?.user_id, entry);
      });

      // Also include leaderboard entries that are NOT in participants (edge case)
      const participantUserIds = new Set((participantsData || []).map(p => p?.user_id));
      const extraLeaderboardEntries = (leaderboardData || [])?.filter(
        entry => !participantUserIds?.has(entry?.user_id)
      );

      // Merge participants with leaderboard data
      const mergedData = (participantsData || [])?.map(participant => {
        const leaderboardEntry = leaderboardMap?.get(participant?.user_id);

        if (leaderboardEntry) {
          // User has leaderboard entry, return it
          return leaderboardEntry;
        } else {
          // User doesn't have leaderboard entry, create default entry with 0 points
          return {
            user_id: participant?.user_id,
            tournament_id: tournamentId,
            rank: null, // Will be assigned after sorting
            total_points: 0,
            correct_predictions: 0,
            total_predictions: 0,
            accuracy: 0,
            tournament_prediction_points: 0,
            match_prediction_points: 0,
            bonus_points: 0,
            confidence_bonus: 0,
            user_profiles: participant?.user_profiles
          };
        }
      });

      // Add any leaderboard entries not in participants list
      extraLeaderboardEntries?.forEach(entry => mergedData?.push(entry));

      // Sort by total_points descending, then accuracy, then correct_predictions (match winner predictions)
      mergedData?.sort((a, b) => {
        if (b?.total_points !== a?.total_points) {
          return b?.total_points - a?.total_points;
        }
        if (b?.accuracy !== a?.accuracy) {
          return b?.accuracy - a?.accuracy;
        }
        return (b?.correct_predictions || 0) - (a?.correct_predictions || 0);
      });

      // Assign ranks to all entries
      mergedData?.forEach((entry, index) => {
        entry.rank = index + 1;
      });

      return toCamelCase(mergedData || []);
    } catch (error) {
      console.error('Leaderboard fetch error:', error);
      throw error;
    }
  },

  async getUserTotalPredictions(userId, tournamentId) {
    try {
      // Count tournament predictions for this tournament
      const { count: tournamentPredCount, error: tournamentError } = await supabase
        ?.from('tournament_predictions')
        ?.select('*', { count: 'exact', head: true })
        ?.eq('user_id', userId)
        ?.eq('tournament_id', tournamentId);

      if (tournamentError) {
        console.error('Tournament predictions count error:', tournamentError);
      }

      // Count match predictions for matches in this tournament
      const { count: matchPredCount, error: matchError } = await supabase
        ?.from('match_predictions')
        ?.select('match_id, matches!inner(tournament_id)', { count: 'exact', head: true })
        ?.eq('user_id', userId)
        ?.eq('matches.tournament_id', tournamentId);

      if (matchError) {
        console.error('Match predictions count error:', matchError);
      }

      let totalPredictions = (tournamentPredCount || 0) + (matchPredCount || 0);
      return totalPredictions;
    } catch (error) {
      console.error('Error fetching user total predictions:', error);
      return 0;
    }
  },

  async getUserRank(tournamentId) {
    try {
      const { data: user } = await supabase?.auth?.getUser();
      if (!user?.user) throw new Error('User not authenticated');

      const { data, error } = await supabase?.from('leaderboard')?.select('*')?.eq('tournament_id', tournamentId)?.eq('user_id', user?.user?.id)?.maybeSingle();

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Error fetching user rank:', error?.message);
        return null;
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('User rank fetch error:', error);
      throw error;
    }
  },

  async getUserLeaderboardEntries(userId) {
    try {
      const { data, error } = await supabase?.from('leaderboard')?.select(`
          *,
          tournaments(name, format, status, start_date, end_date)
        `)?.eq('user_id', userId)?.order('updated_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Error fetching user leaderboard entries:', error?.message);
        return [];
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('User leaderboard entries fetch error:', error);
      return [];
    }
  },

  async getCategoryPointsBreakdown(tournamentId, userId, predictionSettings) {
    try {
      const matchSettings = predictionSettings?.matchLevel || {};
      const bonusSettings = predictionSettings?.bonusPoints || {};
      const tournamentSettings = predictionSettings?.tournamentLevel || {};

      // Fetch all match predictions for this user in this tournament, joined with match results
      const { data: matchPreds, error } = await supabase
        ?.from('match_predictions')
        ?.select(`
          *,
          matches!inner(
            tournament_id,
            winner_id,
            toss_winner_id,
            batting_first_id,
            man_of_match_id,
            highest_scorer_id,
            highest_wicket_taker_id,
            status
          )
        `)
        ?.eq('user_id', userId)
        ?.eq('matches.tournament_id', tournamentId);

      if (error) {
        console.error('Category breakdown fetch error:', error);
        return null;
      }

      const breakdown = {
        matchWinner: 0,
        tossWinner: 0,
        battingFirst: 0,
        manOfMatch: 0,
        highestRunScorer: 0,
        highestWicketTaker: 0,
        confidenceBonus: 0,
        bonusPoints: 0,
        champion: 0,
        finalists: 0,
        top4: 0,
        superOver: 0,
        playerOfTournament: 0,
      };

      const highConf = bonusSettings?.confidenceBonus?.highConfidence || 10;
      const medConf = bonusSettings?.confidenceBonus?.mediumConfidence || 5;
      const confEnabled = bonusSettings?.confidenceBonus?.enabled;

      for (const pred of matchPreds || []) {
        const match = pred?.matches;
        if (!match || match?.status !== 'completed') continue;

        // Match Winner
        if (matchSettings?.matchWinner?.enabled && pred?.winner_id && match?.winner_id) {
          if (pred?.winner_id === match?.winner_id) {
            breakdown.matchWinner += matchSettings?.matchWinner?.points || 10;
            if (confEnabled && pred?.winner_confidence) {
              if (pred?.winner_confidence >= 4) breakdown.confidenceBonus += highConf;
              else if (pred?.winner_confidence >= 2) breakdown.confidenceBonus += medConf;
            }
          }
        }

        // Toss Winner
        if (matchSettings?.tossWinner?.enabled && pred?.toss_winner_id && match?.toss_winner_id) {
          if (pred?.toss_winner_id === match?.toss_winner_id) {
            breakdown.tossWinner += matchSettings?.tossWinner?.points || 5;
          }
        }

        // Batting First
        if (matchSettings?.battingFirst?.enabled && pred?.batting_first_id && match?.batting_first_id) {
          if (pred?.batting_first_id === match?.batting_first_id) {
            breakdown.battingFirst += matchSettings?.battingFirst?.points || 5;
          }
        }

        // Man of the Match
        if (matchSettings?.manOfMatch?.enabled && pred?.man_of_match_id && match?.man_of_match_id) {
          if (pred?.man_of_match_id === match?.man_of_match_id) {
            breakdown.manOfMatch += matchSettings?.manOfMatch?.points || 15;
            if (confEnabled && pred?.motm_confidence) {
              if (pred?.motm_confidence >= 4) breakdown.confidenceBonus += highConf;
              else if (pred?.motm_confidence >= 2) breakdown.confidenceBonus += medConf;
            }
          }
        }

        // Highest Run Scorer
        if (matchSettings?.highestRunScorer?.enabled && pred?.highest_scorer_id && match?.highest_scorer_id) {
          if (pred?.highest_scorer_id === match?.highest_scorer_id) {
            breakdown.highestRunScorer += matchSettings?.highestRunScorer?.points || 20;
            if (confEnabled && pred?.scorer_confidence) {
              if (pred?.scorer_confidence >= 4) breakdown.confidenceBonus += highConf;
              else if (pred?.scorer_confidence >= 2) breakdown.confidenceBonus += medConf;
            }
          }
        }

        // Highest Wicket Taker
        if (matchSettings?.highestWicketTaker?.enabled && pred?.highest_wicket_taker_id && match?.highest_wicket_taker_id) {
          if (pred?.highest_wicket_taker_id === match?.highest_wicket_taker_id) {
            breakdown.highestWicketTaker += matchSettings?.highestWicketTaker?.points || 20;
            if (confEnabled && pred?.wicket_confidence) {
              if (pred?.wicket_confidence >= 4) breakdown.confidenceBonus += highConf;
              else if (pred?.wicket_confidence >= 2) breakdown.confidenceBonus += medConf;
            }
          }
        }
      }

      // Fetch tournament result and user's tournament prediction for tournament-level breakdown
      const [tournamentResult, userTournamentPred] = await Promise.all([
        supabase
          ?.from('tournaments')
          ?.select('champion_team_id, finalist1_id, finalist2_id, semifinalist1_id, semifinalist2_id, semifinalist3_id, semifinalist4_id, top4_team1_id, top4_team2_id, top4_team3_id, top4_team4_id, super_over_team_ids, player_of_tournament_id, tournament_results_status')
          ?.eq('id', tournamentId)
          ?.maybeSingle(),
        supabase
          ?.from('tournament_predictions')
          ?.select('champion_team_id, finalist1_id, finalist2_id, semifinalist1_id, semifinalist2_id, semifinalist3_id, semifinalist4_id, top4_team1_id, top4_team2_id, top4_team3_id, top4_team4_id, super_over_team_id, player_of_tournament_id')
          ?.eq('tournament_id', tournamentId)
          ?.eq('user_id', userId)
          ?.maybeSingle()
      ]);

      const tResult = tournamentResult?.data;
      const uPred = userTournamentPred?.data;

      if (tResult && uPred && (tResult?.tournament_results_status === 'published' || tResult?.tournament_results_status === 'partial')) {
        // Champion
        if (tournamentSettings?.championPrediction?.enabled && uPred?.champion_team_id && tResult?.champion_team_id) {
          if (uPred?.champion_team_id === tResult?.champion_team_id) {
            breakdown.champion += tournamentSettings?.championPrediction?.points || 50;
          }
        }

        // Finalists
        if (tournamentSettings?.finalistTeams?.enabled && tResult?.finalist1_id && tResult?.finalist2_id) {
          const actualFinalists = [tResult?.finalist1_id, tResult?.finalist2_id]?.filter(Boolean);
          const predictedFinalists = [uPred?.finalist1_id, uPred?.finalist2_id]?.filter(Boolean);
          const finalistPoints = tournamentSettings?.finalistTeams?.points || 25;
          predictedFinalists?.forEach(pf => {
            if (actualFinalists?.includes(pf)) {
              breakdown.finalists += finalistPoints;
            }
          });
        }

        // Top 4
        if (tournamentSettings?.top4Teams?.enabled) {
          const actualTop4 = [tResult?.top4_team1_id, tResult?.top4_team2_id, tResult?.top4_team3_id, tResult?.top4_team4_id]?.filter(Boolean);
          const predictedTop4 = [uPred?.top4_team1_id, uPred?.top4_team2_id, uPred?.top4_team3_id, uPred?.top4_team4_id]?.filter(Boolean);
          const top4Points = tournamentSettings?.top4Teams?.points || 10;
          if (actualTop4?.length > 0) {
            predictedTop4?.forEach(pt => {
              if (actualTop4?.includes(pt)) {
                breakdown.top4 += top4Points;
              }
            });

            // Additional points for each correct order position
            for (let i = 0; i < 4; i++) {
              if (predictedTop4?.[i] && actualTop4?.[i] && predictedTop4?.[i] === actualTop4?.[i]) {
                breakdown.top4 += tournamentSettings?.top4Teams?.orderPoints || 5;
              }
            }
          }
        }

        // Super Over
        if (tournamentSettings?.superOverTeam?.enabled && uPred?.super_over_team_id) {
          // super_over_team_id on tournament may be an array (from migration); handle both
          const actualSuperOver = tResult?.super_over_team_ids || [];
          const superOverPoints = tournamentSettings?.superOverTeam?.points || 15;
          if (Array.isArray(actualSuperOver)) {
            if (actualSuperOver?.includes(uPred?.super_over_team_id)) {
              breakdown.superOver += superOverPoints;
            }
          } else if (actualSuperOver && uPred?.super_over_team_id === actualSuperOver) {
            breakdown.superOver += superOverPoints;
          }
        }

        // Player of Tournament
        if (tournamentSettings?.playerOfTournament?.enabled && uPred?.player_of_tournament_id && tResult?.player_of_tournament_id) {
          if (uPred?.player_of_tournament_id === tResult?.player_of_tournament_id) {
            breakdown.playerOfTournament += tournamentSettings?.playerOfTournament?.points || 30;
          }
        }
      }

      return breakdown;
    } catch (error) {
      console.error('Category breakdown error:', error);
      return null;
    }
  },

  /**
   * Subscribe to real-time leaderboard updates for a tournament
   * @param {string} tournamentId - Tournament ID to subscribe to
   * @param {function} callback - Callback function to handle updates
   * @returns {object} Subscription object with unsubscribe method
   */
  subscribeToLeaderboard(tournamentId, callback) {
    try {
      const channel = supabase
        ?.channel(`leaderboard:${tournamentId}`)
        ?.on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'leaderboard',
            filter: `tournament_id=eq.${tournamentId}`
          },
          async (payload) => {
            // Fetch updated leaderboard data
            const updatedData = await leaderboardService?.getByTournament(tournamentId);
            callback(updatedData);
          }
        )
        ?.subscribe();

      return {
        unsubscribe: () => {
          supabase?.removeChannel(channel);
        }
      };
    } catch (error) {
      console.error('Leaderboard subscription error:', error);
      return {
        unsubscribe: () => { }
      };
    }
  }
};

// ============================================
// TEAMS
// ============================================

export const teamsService = {
  async getAll() {
    try {
      const { data, error } = await supabase?.from('teams')?.select('*')?.order('name', { ascending: true });

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Error fetching teams:', error?.message);
        return [];
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Teams fetch error:', error);
      throw error;
    }
  },

  async getAllWithStats() {
    try {
      const { data, error } = await supabase?.from('teams')?.select(`
          *,
          tournament_teams(count),
          players(count)
        `)?.order('name', { ascending: true });

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Error fetching teams with stats:', error?.message);
        return [];
      }

      return data?.map(team => ({
        ...toCamelCase(team),
        tournamentCount: team?.tournament_teams?.[0]?.count || 0,
        playerCount: team?.players?.[0]?.count || 0
      })) || [];
    } catch (error) {
      console.error('Teams with stats fetch error:', error);
      throw error;
    }
  },

  async getLeagueTeams() {
    try {
      const { data, error } = await supabase?.from('teams')?.select(`
          *,
          tournament_teams(count),
          players(count)
        `)?.eq('team_type', 'league')?.order('name', { ascending: true });

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Error fetching league teams:', error?.message);
        return [];
      }

      return data?.map(team => ({
        ...toCamelCase(team),
        tournamentCount: team?.tournament_teams?.[0]?.count || 0,
        playerCount: team?.players?.[0]?.count || 0
      })) || [];
    } catch (error) {
      console.error('League teams fetch error:', error);
      throw error;
    }
  },

  async getTournamentHistory(teamId) {
    try {
      const { data, error } = await supabase?.from('tournament_teams')?.select(`
          tournaments(*)
        `)?.eq('team_id', teamId)?.order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Error fetching tournament history:', error?.message);
        return [];
      }

      return toCamelCase(data?.map(tt => tt?.tournaments) || []);
    } catch (error) {
      console.error('Tournament history fetch error:', error);
      throw error;
    }
  },

  async getByName(teamName) {
    try {
      const { data, error } = await supabase?.from('teams')?.select('*')?.eq('name', teamName)?.maybeSingle();

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Error fetching team by name:', error?.message);
        return null;
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Team fetch by name error:', error);
      throw error;
    }
  },

  async getByTournament(tournamentId) {
    try {
      if (!supabase) {
        console.error('Supabase client not initialized');
        return [];
      }

      if (!tournamentId) {
        console.error('Tournament ID is required');
        return [];
      }

      const { data, error } = await supabase?.from('tournament_teams')?.select(`
          *,
          teams(*)
        `)?.eq('tournament_id', tournamentId);

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Error fetching tournament teams:', error?.message);
        return [];
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Tournament teams fetch error:', error);
      // Return empty array instead of throwing to prevent app crash
      return [];
    }
  },

  async create(teamData) {
    try {
      const snakeData = toSnakeCase(teamData);

      const { data, error } = await supabase?.from('teams')?.insert(snakeData)?.select()?.single();

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Error creating team:', error?.message);
        return null;
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Team creation error:', error);
      throw error;
    }
  },

  async update(teamId, teamData) {
    try {
      const snakeData = toSnakeCase(teamData);

      const { data, error } = await supabase?.from('teams')?.update(snakeData)?.eq('id', teamId)?.select()?.single();

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Error updating team:', error?.message);
        return null;
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Team update error:', error);
      throw error;
    }
  },

  async delete(teamId) {
    try {
      const { error } = await supabase?.from('teams')?.delete()?.eq('id', teamId);

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Error deleting team:', error?.message);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Team delete error:', error);
      throw error;
    }
  },

  async addToTournament(tournamentId, teamId) {
    try {
      const { data, error } = await supabase?.from('tournament_teams')?.insert({
        tournament_id: tournamentId,
        team_id: teamId
      })?.select()?.single();

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Error adding team to tournament:', error?.message);
        return null;
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Add team to tournament error:', error);
      throw error;
    }
  },

  async removeFromTournament(tournamentId, teamId) {
    try {
      const { error } = await supabase?.from('tournament_teams')?.delete()?.eq('tournament_id', tournamentId)?.eq('team_id', teamId);

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Error removing team from tournament:', error?.message);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Remove team from tournament error:', error);
      throw error;
    }
  }
};

// ============================================
// PLAYERS
// ============================================

export const playersService = {
  async getAll() {
    const { data, error } = await supabase?.from('players')?.select('*, teams(name, team_type)')?.order('name');
    if (error) throw error;
    return data?.map(player => ({
      id: player?.id,
      name: player?.name,
      teamId: player?.team_id,
      teamName: player?.teams?.name,
      teamType: player?.teams?.team_type,
      role: player?.role,
      battingStyle: player?.batting_style,
      bowlingStyle: player?.bowling_style,
      jerseyNumber: player?.jersey_number,
      createdAt: player?.created_at
    })) || [];
  },

  async getByTeam(teamId) {
    const { data, error } = await supabase?.from('players')?.select('*')?.eq('team_id', teamId)?.order('name');
    if (error) throw error;
    return data?.map(player => ({
      id: player?.id,
      name: player?.name,
      teamId: player?.team_id,
      role: player?.role,
      battingStyle: player?.batting_style,
      bowlingStyle: player?.bowling_style,
      jerseyNumber: player?.jersey_number,
      createdAt: player?.created_at
    })) || [];
  },

  async getByTournament(tournamentId) {
    const { data, error } = await supabase
      ?.from('tournament_squads')
      ?.select(`
        player_id,
        team_id,
        players(*),
        teams(name, short_name)
      `)
      ?.eq('tournament_id', tournamentId);

    if (error) throw error;

    return data?.map(item => ({
      id: item?.players?.id,
      name: item?.players?.name,
      teamId: item?.team_id,
      teamName: item?.teams?.name,
      teamShortName: item?.teams?.short_name || item?.teams?.name?.substring(0, 3)?.toUpperCase(),
      role: item?.players?.role,
      battingStyle: item?.players?.batting_style,
      bowlingStyle: item?.players?.bowling_style,
      jerseyNumber: item?.players?.jersey_number,
      createdAt: item?.players?.created_at
    })) || [];
  },

  async create(playerData) {
    const { data, error } = await supabase?.from('players')?.insert([{
      name: playerData?.name,
      team_id: playerData?.teamId,
      role: playerData?.role,
      batting_style: playerData?.battingStyle,
      bowling_style: playerData?.bowlingStyle,
      jersey_number: playerData?.jerseyNumber === '' ? null : playerData?.jerseyNumber
    }])?.select()?.single();
    if (error) throw error;
    return {
      id: data?.id,
      name: data?.name,
      teamId: data?.team_id,
      role: data?.role,
      battingStyle: data?.batting_style,
      bowlingStyle: data?.bowling_style,
      jerseyNumber: data?.jersey_number,
      createdAt: data?.created_at
    };
  },

  async update(id, playerData) {
    const { data, error } = await supabase?.from('players')?.update({
      name: playerData?.name,
      team_id: playerData?.teamId,
      role: playerData?.role,
      batting_style: playerData?.battingStyle,
      bowling_style: playerData?.bowlingStyle,
      jersey_number: playerData?.jerseyNumber === '' ? null : playerData?.jerseyNumber
    })?.eq('id', id)?.select()?.single();
    if (error) throw error;
    return {
      id: data?.id,
      name: data?.name,
      teamId: data?.team_id,
      role: data?.role,
      battingStyle: data?.batting_style,
      bowlingStyle: data?.bowling_style,
      jerseyNumber: data?.jersey_number,
      createdAt: data?.created_at
    };
  },

  async delete(playerId) {
    try {
      const { error } = await supabase?.from('players')?.delete()?.eq('id', playerId);

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Error deleting player:', error?.message);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Player deletion error:', error);
      throw error;
    }
  },

  async getTournamentSquad(tournamentId, teamId) {
    try {
      const { data, error } = await supabase?.from('tournament_squads')?.select(`
          *,
          players(*)
        `)?.eq('tournament_id', tournamentId)?.eq('team_id', teamId);

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Error fetching tournament squad:', error?.message);
        return [];
      }

      return data?.map(ts => ({
        ...toCamelCase(ts?.players),
        playerId: ts?.player_id
      })) || [];
    } catch (error) {
      console.error('Tournament squad fetch error:', error);
      throw error;
    }
  },

  async addToTournamentSquad(tournamentId, teamId, playerId) {
    try {
      const { data, error } = await supabase?.from('tournament_squads')?.insert({
        tournament_id: tournamentId,
        team_id: teamId,
        player_id: playerId
      })?.select()?.single();

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Error adding player to squad:', error?.message);
        return null;
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Add to squad error:', error);
      throw error;
    }
  },

  async removeFromTournamentSquad(tournamentId, teamId, playerId) {
    try {
      const { error } = await supabase?.from('tournament_squads')?.delete()?.eq('tournament_id', tournamentId)?.eq('team_id', teamId)?.eq('player_id', playerId);

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Error removing player from squad:', error?.message);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Remove from squad error:', error);
      throw error;
    }
  }
};

// ============================================
// PLAYER TEAM ASSIGNMENTS
// ============================================

export const playerTeamAssignmentsService = {
  async getByTeam(teamId) {
    try {
      const { data, error } = await supabase
        ?.from('player_team_assignments')
        ?.select(`
          *,
          players(id, name, role, batting_style, bowling_style, jersey_number, team_id)
        `)
        ?.eq('team_id', teamId)
        ?.order('effective_from', { ascending: false });

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Error fetching team assignments:', error?.message);
        return [];
      }

      return data?.map(a => ({
        id: a?.id,
        playerId: a?.player_id,
        teamId: a?.team_id,
        effectiveFrom: a?.effective_from,
        effectiveUntil: a?.effective_until,
        transferReason: a?.transfer_reason,
        season: a?.season,
        isActive: a?.is_active,
        createdAt: a?.created_at,
        player: a?.players ? {
          id: a?.players?.id,
          name: a?.players?.name,
          role: a?.players?.role,
          battingStyle: a?.players?.batting_style,
          bowlingStyle: a?.players?.bowling_style,
          jerseyNumber: a?.players?.jersey_number,
          teamId: a?.players?.team_id
        } : null
      })) || [];
    } catch (error) {
      console.error('Team assignments fetch error:', error);
      throw error;
    }
  },

  async getByPlayer(playerId) {
    try {
      const { data, error } = await supabase
        ?.from('player_team_assignments')
        ?.select(`
          *,
          teams(id, name, logo, team_type, league_name)
        `)
        ?.eq('player_id', playerId)
        ?.order('effective_from', { ascending: false });

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Error fetching player assignments:', error?.message);
        return [];
      }

      return data?.map(a => ({
        id: a?.id,
        playerId: a?.player_id,
        teamId: a?.team_id,
        effectiveFrom: a?.effective_from,
        effectiveUntil: a?.effective_until,
        transferReason: a?.transfer_reason,
        season: a?.season,
        isActive: a?.is_active,
        createdAt: a?.created_at,
        team: a?.teams ? {
          id: a?.teams?.id,
          name: a?.teams?.name,
          logo: a?.teams?.logo,
          teamType: a?.teams?.team_type,
          leagueName: a?.teams?.league_name
        } : null
      })) || [];
    } catch (error) {
      console.error('Player assignments fetch error:', error);
      throw error;
    }
  },

  async getActiveByTeam(teamId) {
    try {
      const { data, error } = await supabase
        ?.from('player_team_assignments')
        ?.select(`
          *,
          players(id, name, role, batting_style, bowling_style, jersey_number, team_id)
        `)
        ?.eq('team_id', teamId)
        ?.eq('is_active', true)
        ?.order('effective_from', { ascending: false });

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Error fetching active team assignments:', error?.message);
        return [];
      }

      return data?.map(a => ({
        id: a?.id,
        playerId: a?.player_id,
        teamId: a?.team_id,
        effectiveFrom: a?.effective_from,
        effectiveUntil: a?.effective_until,
        transferReason: a?.transfer_reason,
        season: a?.season,
        isActive: a?.is_active,
        createdAt: a?.created_at,
        player: a?.players ? {
          id: a?.players?.id,
          name: a?.players?.name,
          role: a?.players?.role,
          battingStyle: a?.players?.batting_style,
          bowlingStyle: a?.players?.bowling_style,
          jerseyNumber: a?.players?.jersey_number,
          teamId: a?.players?.team_id
        } : null
      })) || [];
    } catch (error) {
      console.error('Active team assignments fetch error:', error);
      throw error;
    }
  },

  async assign(assignmentData) {
    try {
      // Deactivate any existing active assignment for this player in this team
      await supabase
        ?.from('player_team_assignments')
        ?.update({
          is_active: false,
          effective_until: assignmentData?.effectiveFrom || new Date()?.toISOString()?.split('T')?.[0],
          updated_at: new Date()?.toISOString()
        })
        ?.eq('player_id', assignmentData?.playerId)
        ?.eq('team_id', assignmentData?.teamId)
        ?.eq('is_active', true);

      const { data, error } = await supabase
        ?.from('player_team_assignments')
        ?.insert({
          player_id: assignmentData?.playerId,
          team_id: assignmentData?.teamId,
          effective_from: assignmentData?.effectiveFrom || new Date()?.toISOString()?.split('T')?.[0],
          effective_until: assignmentData?.effectiveUntil || null,
          transfer_reason: assignmentData?.transferReason || null,
          season: assignmentData?.season || null,
          is_active: true
        })
        ?.select()
        ?.single();

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Error creating assignment:', error?.message);
        return null;
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Assignment creation error:', error);
      throw error;
    }
  },

  async transfer(playerId, fromTeamId, toTeamId, transferData) {
    try {
      const today = new Date()?.toISOString()?.split('T')?.[0];

      // Close the old assignment
      await supabase
        ?.from('player_team_assignments')
        ?.update({
          is_active: false,
          effective_until: transferData?.effectiveFrom || today,
          updated_at: new Date()?.toISOString()
        })
        ?.eq('player_id', playerId)
        ?.eq('team_id', fromTeamId)
        ?.eq('is_active', true);

      // Create new assignment
      const { data, error } = await supabase
        ?.from('player_team_assignments')
        ?.insert({
          player_id: playerId,
          team_id: toTeamId,
          effective_from: transferData?.effectiveFrom || today,
          effective_until: transferData?.effectiveUntil || null,
          transfer_reason: transferData?.transferReason || null,
          season: transferData?.season || null,
          is_active: true
        })
        ?.select()
        ?.single();

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Error creating transfer assignment:', error?.message);
        return null;
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Transfer error:', error);
      throw error;
    }
  },

  async deactivate(assignmentId) {
    try {
      const { data, error } = await supabase
        ?.from('player_team_assignments')
        ?.update({
          is_active: false,
          effective_until: new Date()?.toISOString()?.split('T')?.[0],
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', assignmentId)
        ?.select()
        ?.single();

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Error deactivating assignment:', error?.message);
        return null;
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('Deactivate assignment error:', error);
      throw error;
    }
  }
};

// ============================================
// USER MANAGEMENT
// ============================================

export const userManagementService = {
  async getUserStats(userId) {
    try {
      // Get user's tournament participations
      const { data: participations, error } = await supabase
        ?.from('tournament_participants')?.select('tournament_id, tournaments(name, status)')?.eq('user_id', userId);

      if (error) {
        console.error('Error fetching participations:', error?.message);
      }

      // Count tournament predictions
      const { count: tournamentPredCount, error: tournamentPredError } = await supabase
        ?.from('tournament_predictions')?.select('*', { count: 'exact', head: true })
        ?.eq('user_id', userId);

      if (tournamentPredError) {
        console.error('Tournament predictions count error:', tournamentPredError);
      }

      // Count match predictions
      const { count: matchPredCount, error: matchError } = await supabase
        ?.from('match_predictions')?.select('*', { count: 'exact', head: true })
        ?.eq('user_id', userId);

      if (matchError) {
        console.error('Match predictions count error:', matchError);
      }

      let totalPredictions = (tournamentPredCount || 0) + (matchPredCount || 0);
      return totalPredictions;
    } catch (error) {
      console.error('Error fetching user total predictions:', error);
      return 0;
    }
  },

  async getUserRank(tournamentId) {
    try {
      const { data: user } = await supabase?.auth?.getUser();
      if (!user?.user) throw new Error('User not authenticated');

      const { data, error } = await supabase?.from('leaderboard')?.select('*')?.eq('tournament_id', tournamentId)?.eq('user_id', user?.user?.id)?.maybeSingle();

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Error fetching user rank:', error?.message);
        return null;
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('User rank fetch error:', error);
      throw error;
    }
  },

  async getUserLeaderboardEntries(userId) {
    try {
      const { data, error } = await supabase?.from('leaderboard')?.select(`
          *,
          tournaments(name, format, status, start_date, end_date)
        `)?.eq('user_id', userId)?.order('updated_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) throw error;
        console.error('Error fetching user leaderboard entries:', error?.message);
        return [];
      }

      return toCamelCase(data);
    } catch (error) {
      console.error('User leaderboard entries fetch error:', error);
      return [];
    }
  },

  async getCategoryPointsBreakdown(tournamentId, userId, predictionSettings) {
    try {
      const matchSettings = predictionSettings?.matchLevel || {};
      const bonusSettings = predictionSettings?.bonusPoints || {};

      // Fetch all match predictions for this user in this tournament, joined with match results
      const { data: matchPreds, error } = await supabase
        ?.from('match_predictions')
        ?.select(`
          *,
          matches!inner(
            tournament_id,
            winner_id,
            toss_winner_id,
            batting_first_id,
            man_of_match_id,
            highest_scorer_id,
            highest_wicket_taker_id,
            status
          )
        `)
        ?.eq('user_id', userId)
        ?.eq('matches.tournament_id', tournamentId);

      if (error) {
        console.error('Category breakdown fetch error:', error);
        return null;
      }

      const breakdown = {
        matchWinner: 0,
        tossWinner: 0,
        battingFirst: 0,
        manOfMatch: 0,
        highestRunScorer: 0,
        highestWicketTaker: 0,
        confidenceBonus: 0,
        bonusPoints: 0,
      };

      const highConf = bonusSettings?.confidenceBonus?.highConfidence || 10;
      const medConf = bonusSettings?.confidenceBonus?.mediumConfidence || 5;
      const confEnabled = bonusSettings?.confidenceBonus?.enabled;

      for (const pred of matchPreds || []) {
        const match = pred?.matches;
        if (!match || match?.status !== 'completed') continue;

        // Match Winner
        if (matchSettings?.matchWinner?.enabled && pred?.winner_id && match?.winner_id) {
          if (pred?.winner_id === match?.winner_id) {
            breakdown.matchWinner += matchSettings?.matchWinner?.points || 10;
            if (confEnabled && pred?.winner_confidence) {
              if (pred?.winner_confidence >= 4) breakdown.confidenceBonus += highConf;
              else if (pred?.winner_confidence >= 2) breakdown.confidenceBonus += medConf;
            }
          }
        }

        // Toss Winner
        if (matchSettings?.tossWinner?.enabled && pred?.toss_winner_id && match?.toss_winner_id) {
          if (pred?.toss_winner_id === match?.toss_winner_id) {
            breakdown.tossWinner += matchSettings?.tossWinner?.points || 5;
          }
        }

        // Batting First
        if (matchSettings?.battingFirst?.enabled && pred?.batting_first_id && match?.batting_first_id) {
          if (pred?.batting_first_id === match?.batting_first_id) {
            breakdown.battingFirst += matchSettings?.battingFirst?.points || 5;
          }
        }

        // Man of the Match
        if (matchSettings?.manOfMatch?.enabled && pred?.man_of_match_id && match?.man_of_match_id) {
          if (pred?.man_of_match_id === match?.man_of_match_id) {
            breakdown.manOfMatch += matchSettings?.manOfMatch?.points || 15;
            if (confEnabled && pred?.motm_confidence) {
              if (pred?.motm_confidence >= 4) breakdown.confidenceBonus += highConf;
              else if (pred?.motm_confidence >= 2) breakdown.confidenceBonus += medConf;
            }
          }
        }

        // Highest Run Scorer
        if (matchSettings?.highestRunScorer?.enabled && pred?.highest_scorer_id && match?.highest_scorer_id) {
          if (pred?.highest_scorer_id === match?.highest_scorer_id) {
            breakdown.highestRunScorer += matchSettings?.highestRunScorer?.points || 20;
            if (confEnabled && pred?.scorer_confidence) {
              if (pred?.scorer_confidence >= 4) breakdown.confidenceBonus += highConf;
              else if (pred?.scorer_confidence >= 2) breakdown.confidenceBonus += medConf;
            }
          }
        }

        // Highest Wicket Taker
        if (matchSettings?.highestWicketTaker?.enabled && pred?.highest_wicket_taker_id && match?.highest_wicket_taker_id) {
          if (pred?.highest_wicket_taker_id === match?.highest_wicket_taker_id) {
            breakdown.highestWicketTaker += matchSettings?.highestWicketTaker?.points || 20;
            if (confEnabled && pred?.wicket_confidence) {
              if (pred?.wicket_confidence >= 4) breakdown.confidenceBonus += highConf;
              else if (pred?.wicket_confidence >= 2) breakdown.confidenceBonus += medConf;
            }
          }
        }
      }

      return breakdown;
    } catch (error) {
      console.error('Category breakdown error:', error);
      return null;
    }
  },

  /**
   * Subscribe to real-time leaderboard updates for a tournament
   * @param {string} tournamentId - Tournament ID to subscribe to
   * @param {function} callback - Callback function to handle updates
   * @returns {object} Subscription object with unsubscribe method
   */
  subscribeToLeaderboard(tournamentId, callback) {
    try {
      const channel = supabase
        ?.channel(`leaderboard:${tournamentId}`)
        ?.on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'leaderboard',
            filter: `tournament_id=eq.${tournamentId}`
          },
          async (payload) => {
            // Fetch updated leaderboard data
            const updatedData = await leaderboardService?.getByTournament(tournamentId);
            callback(updatedData);
          }
        )
        ?.subscribe();

      return {
        unsubscribe: () => {
          supabase?.removeChannel(channel);
        }
      };
    } catch (error) {
      console.error('Leaderboard subscription error:', error);
      return {
        unsubscribe: () => { }
      };
    }
  }
};